import { createClient } from '@supabase/supabase-js';

/**
 * Cliente do Supabase Auth.
 *
 * A chave `anon` e publicavel: ela vai no bundle do navegador em qualquer
 * app Supabase. Quem protege os dados e a validacao do JWT na API
 * (api/auth.py) e o RLS -- nunca o sigilo desta chave. A `service_role`,
 * essa sim secreta, jamais pode chegar aqui.
 *
 * `persistSession` guarda a sessao em localStorage e renova o token
 * sozinho antes de expirar, entao o usuario nao e deslogado no meio do
 * uso.
 */
const url = import.meta.env.VITE_SUPABASE_URL;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const authConfigurado = Boolean(url && anon);

if (!authConfigurado) {
  // Sem as variaveis, o app cai no modo aberto da API -- que responde
  // como o primeiro admin. Util em desenvolvimento, mas tem de ser
  // visivel.
  console.warn(
    '[auth] VITE_SUPABASE_URL/ANON_KEY ausentes: sem login. ' +
    'A API precisa estar em modo aberto (AUTH_ATIVA=false).',
  );
}

/**
 * A pessoa chegou pelo link de REDEFINICAO de senha (e nao por convite)?
 *
 * Os dois links aterrissam em /definir-acesso -- e o endereco que o
 * Supabase ja aceita como retorno --, mas o destino depois e outro: o
 * convidado segue para completar o cadastro; quem so trocou a senha volta
 * para o app. O link traz `type=recovery` no fragmento, e o cliente do
 * Supabase APAGA o fragmento ao trocar o token pela sessao: por isso a
 * leitura acontece aqui, antes de o cliente existir.
 */
export const chegouParaRedefinirSenha =
  typeof window !== 'undefined' && window.location.hash.includes('type=recovery');

/**
 * O link de e-mail voltou com ERRO (vencido, ja usado)? O Supabase
 * redireciona com `error_code` no fragmento -- e, como o `type=recovery`
 * acima, o cliente apaga o fragmento ao processa-lo. Lido aqui, antes.
 */
export const erroNoLinkDeEmail: string | null = (() => {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(
    window.location.hash.replace(/^#/, '') || window.location.search);
  return p.get('error_code') ?? p.get('error');
})();

export const supabase = createClient(url ?? 'http://localhost', anon ?? 'anon', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/** Token da sessao atual, ou null. Usado no header das chamadas a API. */
export async function tokenAtual(): Promise<string | null> {
  if (!authConfigurado) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
